import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import type { ExperienceFormValues } from 'app/components/commons/Experience/ExperienceForm';
import type { ExperienceCategory } from 'app/redux/models/ExperienceCategory/ExperienceCategory';
import type {
  Experience,
  ExperiencesFormQueryParam,
} from 'app/typings/experiences';
import type { ExtranetExperience } from 'app/typings/packages';
import { apiMutation, apiRequest } from 'app/utils/request/api';

const pathExperiencesCategories = '/experiences/categories' as const;

const pathExperiences = '/experiences' as const;
const crudPathExperiences = '/experiences/:experienceId' as const;
const publishPathExperiences = '/experiences/:experienceId/publish' as const;

export const pathExperiencesByHotelId =
  '/hotels/:hotelId/experiences?includeStaycationExp=false' as const;

export function useGetExperiencesById(
  experienceId?: number,
  isLegacyModel?: boolean
) {
  return useQuery({
    queryKey: [crudPathExperiences, experienceId],
    queryFn: () =>
      apiRequest({
        path: crudPathExperiences,
        params: { experienceId: `${experienceId}` },
      }) as Promise<Experience>,
    enabled: !!experienceId && isLegacyModel,
    refetchOnWindowFocus: false,
  });
}

export function useExperiences(queryParams: ExperiencesFormQueryParam) {
  const queryKey = [pathExperiences, { queryParams }];

  return useQuery({
    queryKey: queryKey,
    queryFn: () =>
      apiRequest({
        path: pathExperiences,
        queryParams,
      }) as Promise<Experience[]>,
    onError: () => message.error('Error fetching Experiences'),
    keepPreviousData: true,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
  });
}

export function useDeleteExperience(experienceId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () =>
      apiMutation('DELETE', {
        path: crudPathExperiences,
        params: { experienceId: `${experienceId}` },
      }) as Promise<void>,
    onSuccess: () => {
      message.success(`Experience ${experienceId} deleted`);
      queryClient.invalidateQueries([pathExperiences]);
    },
    onError: () => {
      message.error('Not possible: this experience is used somewhere else');
    },
  });
}

export function useExperiencesByHotelId(hotelId?: number) {
  return useQuery({
    queryKey: [pathExperiencesByHotelId],
    queryFn: () =>
      apiRequest({
        path: pathExperiencesByHotelId,
        params: { hotelId: `${hotelId}` },
      }) as Promise<ExtranetExperience[]>,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!hotelId,
  });
}

export function useExperiencesCategories() {
  return useQuery({
    queryKey: [pathExperiencesCategories],
    queryFn: () =>
      apiRequest({
        path: pathExperiencesCategories,
      }) as Promise<ExperienceCategory[]>,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
  });
}

export function usePublishOldExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (experienceId: number) =>
      apiMutation('PATCH', {
        path: publishPathExperiences,
        params: {
          experienceId: `${experienceId}`,
        },
      }) as Promise<{ published: boolean }>,
    onSuccess: (_, experienceId) => {
      message.success(`Experience ${experienceId} saved`);
      queryClient.invalidateQueries([pathExperiences]);
    },
    onError: (_, experienceId) => {
      message.error(`Error while modifying experience #${experienceId}`);
    },
  });
}

export function useSaveExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ExperienceFormValues) =>
      apiMutation(
        'POST',
        {
          path: pathExperiences,
        },
        payload
      ) as Promise<number>,
    onSuccess: (experienceId) => {
      queryClient.invalidateQueries([pathExperiences]);
      queryClient.invalidateQueries([crudPathExperiences, experienceId]);
      message.success(`Successfully saved experience ${experienceId}`);
    },
    onError: () => {
      message.error(`Error saving experience`);
    },
  });
}
